import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PageTemplate from "../components/PageTemplate";
import http from "../api/apiClient";
import { FaListOl, FaRoute, FaRegCalendarAlt } from "react-icons/fa";
import { FaChartLine } from "react-icons/fa6";
import {
  Bar,
  BarChart,
  Area,
  CartesianGrid,
  Cell,
  Brush,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  Sector,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type UsageTopItem = {
  key?: string;
  path?: string;
  page?: string;
  feature?: string;
  count?: number;
  hits?: number;
  total?: number;
};

type DailyLoginItem = {
  date?: string;
  day?: string;
  label?: string;
  users?: number;
  count?: number;
  logins?: number;
  total?: number;
};

type UserDailyLoginItem = {
  day?: string;
  date?: string;
  label?: string;
  count?: number;
  logins?: number;
  total?: number;
};

type UserDailyLoginSummary = {
  userId?: string;
  total?: number;
  days?: number;
  firstDay?: string;
  lastDay?: string;
  firstDate?: string;
  lastDate?: string;
};

type UserEventItem = {
  at?: string;
  createdAt?: string;
  timestamp?: string;
  method?: string;
  status?: number;
  durationMs?: number;
  duration?: number;
  path?: string;
  page?: string;
  feature?: string;
  userId?: string;
};

type TopUserItem = {
  userId?: string;
  events?: number;
  firstSeen?: string;
  lastSeen?: string;
  totalDurationMs?: number;
  sessions?: number;
  totalSessionSeconds?: number;
  avgSessionSeconds?: number;
};

type UsageTimeseriesResponse = {
  days?: string[];
  series?: Array<{ key?: string; counts?: number[] }>;
};

type UserPageDailyUsageItem = {
  day?: string;
  date?: string;
  label?: string;
  page?: string;
  key?: string;
  events?: number;
  requests?: number;
  count?: number;
  hits?: number;
  total?: number;
  totalDurationMs?: number;
  durationMs?: number;
};

const pad2 = (n: number) => String(n).padStart(2, "0");

const toISOZ = (d: Date) => d.toISOString();

const startOfDayUTC = (d: Date) =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0));
const endOfDayUTC = (d: Date) =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999));

const parseISODateInputToUTC = (value: string) => {
  // value from <input type="date" /> is YYYY-MM-DD in local timezone context;
  // interpret as UTC date to keep backend range stable.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const [, yyyy, mm, dd] = m;
  const d = new Date(Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd), 0, 0, 0, 0));
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatShortNl = (isoOrDate?: string) => {
  if (!isoOrDate) return "—";
  const d = new Date(String(isoOrDate));
  if (Number.isNaN(d.getTime())) return String(isoOrDate);
  return `${pad2(d.getUTCDate())}-${pad2(d.getUTCMonth() + 1)}-${d.getUTCFullYear()}`;
};

const normalizeUsageItems = (raw: any): Array<{ label: string; count: number }> => {
  const arr = Array.isArray(raw) ? raw : raw?.items || raw?.entries || raw?.data;
  if (!Array.isArray(arr)) return [];
  return arr
    .map((it: UsageTopItem) => {
      const label =
        it?.key ?? it?.page ?? it?.feature ?? it?.path ?? (typeof it === "string" ? it : null);
      const count = Number(it?.count ?? it?.hits ?? it?.total ?? 0);
      if (!label) return null;
      if (!Number.isFinite(count)) return { label: String(label), count: 0 };
      return { label: String(label), count };
    })
    .filter(Boolean) as Array<{ label: string; count: number }>;
};

const truncateMiddle = (value: string, max = 36) => {
  const s = String(value ?? "");
  if (s.length <= max) return s;
  const keep = Math.max(10, Math.floor((max - 3) / 2));
  return `${s.slice(0, keep)}...${s.slice(-keep)}`;
};

const normalizeDaily = (raw: any): Array<{ date: string; count: number }> => {
  const arr = Array.isArray(raw) ? raw : raw?.items || raw?.entries || raw?.data;
  if (!Array.isArray(arr)) return [];
  return arr
    .map((it: DailyLoginItem) => {
      const date = it?.date ?? it?.day ?? it?.label ?? null;
      const count = Number(it?.users ?? it?.count ?? it?.logins ?? it?.total ?? 0);
      if (!date) return null;
      if (!Number.isFinite(count)) return { date: String(date), count: 0 };
      return { date: String(date), count };
    })
    .filter(Boolean) as Array<{ date: string; count: number }>;
};

const normalizeUserEvents = (raw: any): { items: UserEventItem[]; total: number | null } => {
  const items = (raw?.content || raw?.items || raw?.entries || raw) as any;
  const arr = Array.isArray(items) ? items : [];
  const totalRaw = raw?.totalElements ?? raw?.total ?? raw?.count ?? null;
  const total = typeof totalRaw === "number" && Number.isFinite(totalRaw) ? totalRaw : null;
  return { items: arr, total };
};

const parseDateFromAny = (value?: string): Date | null => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d+(?:\.\d+)?$/.test(raw)) {
    const numeric = Number(raw);
    if (Number.isFinite(numeric)) {
      const ms = numeric > 1e12 ? numeric : numeric * 1000;
      const d = new Date(ms);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }

  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

const dayKeyUTC = (d: Date) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;

const getEventDate = (ev: any): Date | null => {
  const candidate =
    ev?.at ??
    ev?.createdAt ??
    ev?.timestamp ??
    ev?.time ??
    ev?.occurredAt ??
    ev?.eventAt ??
    ev?.created_at ??
    ev?.occurred_at ??
    ev?.event_at ??
    null;
  return parseDateFromAny(candidate == null ? undefined : String(candidate));
};

const formatDateTimeNl = (value?: string) => {
  if (!value) return "—";
  const raw = String(value).trim();
  if (!raw) return "—";

  // Support numeric epoch seconds/millis (optionally with decimals)
  if (/^\d+(?:\.\d+)?$/.test(raw)) {
    const numeric = Number(raw);
    if (Number.isFinite(numeric)) {
      const ms = numeric > 1e12 ? numeric : numeric * 1000;
      const asDate = new Date(ms);
      if (!Number.isNaN(asDate.getTime())) {
        return asDate.toLocaleString("nl-NL", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
    }
  }

  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString("nl-NL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toDateTimeLocal = (iso?: string) => {
  if (!iso) return "";
  const d = new Date(String(iso));
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const hh = pad2(d.getHours());
  const min = pad2(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};

const fromDateTimeLocalToISOZ = (local?: string) => {
  if (!local) return "";
  const d = new Date(String(local));
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
};

const formatDurationSeconds = (seconds?: number | null) => {
  const s = typeof seconds === "number" && Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : null;
  if (s == null) return "—";
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (hh > 0) return `${hh}:${pad2(mm)}:${pad2(ss)}`;
  return `${mm}:${pad2(ss)}`;
};

const formatDurationMsCompact = (ms?: number | null) => {
  const n = typeof ms === "number" && Number.isFinite(ms) ? Math.max(0, ms) : null;
  if (n == null) return "—";
  if (n < 1000) return `${Math.round(n)}ms`;
  if (n < 60_000) return `${(n / 1000).toFixed(1)}s`;
  const totalSeconds = Math.floor(n / 1000);
  const mm = Math.floor(totalSeconds / 60);
  const ss = totalSeconds % 60;
  return `${mm}m ${pad2(ss)}s`;
};

export default function ReportsAnalytics() {
  const { t } = useTranslation();
  const userEventsSectionRef = useRef<HTMLDivElement | null>(null);
  const timeseriesLastActiveLabelRef = useRef<string | null>(null);
  const timeseriesSelectingRef = useRef<{ left?: string; right?: string } | null>(null);

  const todayUTC = useMemo(() => startOfDayUTC(new Date()), []);
  const defaultFromUTC = useMemo(() => {
    const d = new Date(todayUTC.getTime());
    d.setUTCDate(d.getUTCDate() - 6);
    return d;
  }, [todayUTC]);

  const [fromDate, setFromDate] = useState(() => {
    const d = defaultFromUTC;
    return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
  });
  const [toDate, setToDate] = useState(() => {
    const d = todayUTC;
    return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
  });
  const limit = 10;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [topPaths, setTopPaths] = useState<Array<{ label: string; count: number }>>([]);
  const [topPages, setTopPages] = useState<Array<{ label: string; count: number }>>([]);
  const [topFeatures, setTopFeatures] = useState<Array<{ label: string; count: number }>>([]);
  const [dailyLogins, setDailyLogins] = useState<Array<{ date: string; count: number }>>([]);
  const [uniqueUsersDaily, setUniqueUsersDaily] = useState<Array<{ date: string; count: number }>>([]);
  const [uniqueUsersDailyError, setUniqueUsersDailyError] = useState<string | null>(null);
  const uniqueUsersDailyMedian = useMemo(() => {
    if (!uniqueUsersDaily.length) return null;
    const values = uniqueUsersDaily.map((it) => Number(it.count ?? 0)).filter((n) => Number.isFinite(n));
    if (values.length === 0) return null;
    values.sort((a, b) => a - b);
    const mid = Math.floor(values.length / 2);
    if (values.length % 2 === 1) return values[mid];
    return (values[mid - 1] + values[mid]) / 2;
  }, [uniqueUsersDaily]);

  const [idleMinutes, setIdleMinutes] = useState(30);
  const [topUsers, setTopUsers] = useState<TopUserItem[]>([]);
  const [topUsersError, setTopUsersError] = useState<string | null>(null);

  const [timeseriesGroupBy, setTimeseriesGroupBy] = useState<"path" | "page" | "feature">("page");
  const [usageTimeseries, setUsageTimeseries] = useState<UsageTimeseriesResponse | null>(null);
  const [usageTimeseriesError, setUsageTimeseriesError] = useState<string | null>(null);
  const [timeseriesSelectedKey, setTimeseriesSelectedKey] = useState<string | null>(null);
  const [timeseriesBrush, setTimeseriesBrush] = useState<{ startIndex: number; endIndex: number } | null>(null);
  const [timeseriesSelecting, setTimeseriesSelecting] = useState<{ left?: string; right?: string } | null>(null);

  const [userId, setUserId] = useState("");
  const [userEventsFromLocal, setUserEventsFromLocal] = useState("");
  const [userEventsToLocal, setUserEventsToLocal] = useState("");
  const [userEventsPageIndex, setUserEventsPageIndex] = useState(0);
  const [userEventsSize, setUserEventsSize] = useState(50);
  const [userEventsLoading, setUserEventsLoading] = useState(false);
  const [userEventsError, setUserEventsError] = useState<string | null>(null);
  const [userEventsTotal, setUserEventsTotal] = useState<number | null>(null);
  const [userEvents, setUserEvents] = useState<UserEventItem[]>([]);
  const [userEventsExpanded, setUserEventsExpanded] = useState(false);

  const [userPagesLoading, setUserPagesLoading] = useState(false);
  const [userPagesError, setUserPagesError] = useState<string | null>(null);
  const [userPagesCounts, setUserPagesCounts] = useState<Array<{ label: string; count: number }>>([]);
  const [userPagesReportPlacement, setUserPagesReportPlacement] = useState<"topUsersDrawer" | "userEvents" | null>(
    null
  );
  const [topUsersDrawerUserId, setTopUsersDrawerUserId] = useState<string | null>(null);
  const [topUsersDrawerOpen, setTopUsersDrawerOpen] = useState(false);
  const [userDailyLoading, setUserDailyLoading] = useState(false);
  const [userDailyError, setUserDailyError] = useState<string | null>(null);
  const [userDailyCounts, setUserDailyCounts] = useState<Array<{ day: string; count: number }>>([]);
  const [userDailySummary, setUserDailySummary] = useState<UserDailyLoginSummary | null>(null);
  const [userDailyReportPlacement, setUserDailyReportPlacement] = useState<"topUsersDrawer" | "userEvents" | null>(
    null
  );
  const [userDailySplitMode, setUserDailySplitMode] = useState(true);
  const [userDailySplitGroupBy, setUserDailySplitGroupBy] = useState<"page" | "feature" | "path">("page");
  const [userDailySplitLoading, setUserDailySplitLoading] = useState(false);
  const [userDailySplitError, setUserDailySplitError] = useState<string | null>(null);
  const [userDailySplitSeriesKeys, setUserDailySplitSeriesKeys] = useState<string[]>([]);
  const [userDailySplitChartData, setUserDailySplitChartData] = useState<any[]>([]);
  const [userDailySplitLoadedKey, setUserDailySplitLoadedKey] = useState<string | null>(null);
  const [userDailySplitSelectedKey, setUserDailySplitSelectedKey] = useState<string | null>(null);
  const [userDailySplitDurationsByDay, setUserDailySplitDurationsByDay] = useState<
    Record<string, Record<string, number>>
  >({});

  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [users, setUsers] = useState<Array<{ id: string; label: string }>>([]);
  const userLabelById = useMemo(() => new Map(users.map((u) => [u.id, u.label])), [users]);

  const params = useMemo(() => {
    const fromUTC = parseISODateInputToUTC(fromDate);
    const toUTC = parseISODateInputToUTC(toDate);
    if (!fromUTC || !toUTC) return null;
    const from = toISOZ(startOfDayUTC(fromUTC));
    const to = toISOZ(endOfDayUTC(toUTC));
    return { from, to, limit, fromDay: fromDate, toDay: toDate };
  }, [fromDate, toDate, limit]);

  const timeseriesParams = useMemo(() => {
    if (!params) return null;
    return { fromDay: params.fromDay, toDay: params.toDay, groupBy: timeseriesGroupBy, limit };
  }, [params, timeseriesGroupBy]);

	  const load = useCallback(async () => {
	    if (!params) return;
	    setLoading(true);
	    setError(null);
	    setTopUsersError(null);
	    setUsageTimeseriesError(null);
	    setUniqueUsersDailyError(null);
	    try {
	      const results = await Promise.allSettled([
	        http.get("/admin/analytics/usage/top-paths", {
	          params: { from: params.from, to: params.to, limit: params.limit },
	        }),
	        http.get("/admin/analytics/usage/top-pages", {
	          params: { from: params.from, to: params.to, limit: params.limit },
	        }),
	        http.get("/admin/analytics/usage/top-features", {
	          params: { from: params.from, to: params.to, limit: params.limit },
	        }),
	        http.get("/admin/analytics/logins/daily", { params: { fromDay: params.fromDay, toDay: params.toDay } }),
	        http.get("/admin/analytics/logins/daily/unique-users", {
	          params: { fromDay: params.fromDay, toDay: params.toDay },
	        }),
	        http.get("/admin/analytics/users/top", {
	          params: { from: params.from, to: params.to, limit: 5, idleMinutes },
	        }),
	        timeseriesParams
	          ? http.get("/admin/analytics/usage/timeseries", { params: timeseriesParams })
	          : Promise.resolve({ data: null }),
	      ]);

	      const [pathsRes, pagesRes, featsRes, dailyRes, uniqueUsersRes, topUsersRes, timeseriesRes] = results;

      if (pathsRes.status === "fulfilled") setTopPaths(normalizeUsageItems(pathsRes.value?.data));
      else setTopPaths([]);

      if (pagesRes.status === "fulfilled") setTopPages(normalizeUsageItems(pagesRes.value?.data));
      else setTopPages([]);

      if (featsRes.status === "fulfilled") setTopFeatures(normalizeUsageItems(featsRes.value?.data));
      else setTopFeatures([]);

	      if (dailyRes.status === "fulfilled") setDailyLogins(normalizeDaily(dailyRes.value?.data));
	      else setDailyLogins([]);

	      if (uniqueUsersRes.status === "fulfilled") setUniqueUsersDaily(normalizeDaily(uniqueUsersRes.value?.data));
	      else {
	        setUniqueUsersDaily([]);
	        const e: any = uniqueUsersRes.reason;
	        const msg = e?.response?.data?.message || e?.message || null;
	        setUniqueUsersDailyError(typeof msg === "string" ? msg : null);
	      }

	      if (topUsersRes.status === "fulfilled") {
	        setTopUsers(Array.isArray(topUsersRes.value?.data) ? topUsersRes.value.data : topUsersRes.value?.data?.items || []);
	      } else {
	        setTopUsers([]);
        const e: any = topUsersRes.reason;
        const msg = e?.response?.data?.message || e?.message || "Top users failed.";
        setTopUsersError(typeof msg === "string" ? msg : "Top users failed.");
      }

      if (timeseriesRes.status === "fulfilled") {
        setUsageTimeseries(timeseriesRes.value?.data || null);
      } else {
        setUsageTimeseries(null);
        const e: any = timeseriesRes.reason;
        const msg = e?.response?.data?.message || e?.message || "Timeseries failed.";
        setUsageTimeseriesError(typeof msg === "string" ? msg : "Timeseries failed.");
      }

      const failures = results
        .map((r, idx) => ({ r, idx }))
        .filter(({ r }) => r.status === "rejected") as Array<{ r: PromiseRejectedResult; idx: number }>;
	      if (failures.length > 0) {
	        const labels = [
	          "top-paths",
	          "top-pages",
	          "top-features",
	          "logins/daily",
	          "logins/daily/unique-users",
	          "users/top",
	          "usage/timeseries",
	        ];
        const parts = failures.map(({ r, idx }) => {
          const e: any = r.reason;
          const status = e?.response?.status;
          const message = e?.response?.data?.message || e?.message || "failed";
          return `${labels[idx]}${status ? ` (${status})` : ""}: ${message}`;
        });
        setError(parts.join(" • "));
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to load analytics.");
      // best effort: show top-users error separately if present
      const msg = e?.response?.data?.message || e?.message;
      setTopUsersError(typeof msg === "string" ? msg : null);
    } finally {
      setLoading(false);
    }
  }, [idleMinutes, params, timeseriesParams]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!params) return;
    setUserEventsFromLocal(toDateTimeLocal(params.from));
    setUserEventsToLocal(toDateTimeLocal(params.to));
  }, [params?.from, params?.to]);

  useEffect(() => {
    let cancelled = false;
    const loadUsers = async () => {
      setUsersLoading(true);
      setUsersError(null);
      try {
        const res = await http.get("/admin/users");
        const arr = Array.isArray(res?.data) ? res.data : [];
        const mapped = arr
          .map((u: any) => {
            const id = u?.id;
            if (!id) return null;
            const name = [u?.firstName, u?.lastName].filter(Boolean).join(" ").trim();
            const label = name || u?.username || u?.email || String(id);
            const extra = u?.email && label !== u.email ? ` (${u.email})` : "";
            return { id: String(id), label: `${label}${extra}` };
          })
          .filter(Boolean);
        if (!cancelled) setUsers(mapped);
      } catch (e: any) {
        if (!cancelled) {
          setUsers([]);
          setUsersError(
            e?.response?.status === 403
              ? t("reports.analytics.users_forbidden", {
                  defaultValue: "Geen permissie om users op te halen; gebruik handmatig een userId.",
                })
              : null
          );
        }
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    };
    loadUsers();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const loadUserEvents = useCallback(
    async (opts?: { pageIndex?: number; userIdOverride?: string }) => {
      const trimmed = String(opts?.userIdOverride ?? userId).trim();
      if (!trimmed) return;
      const pageIndex =
        typeof opts?.pageIndex === "number" ? opts.pageIndex : userEventsPageIndex;
      setUserEventsLoading(true);
      setUserEventsError(null);
      try {
        const res = await http.get(`/admin/analytics/users/${encodeURIComponent(trimmed)}/events`, {
          params: {
            from: fromDateTimeLocalToISOZ(userEventsFromLocal) || undefined,
            to: fromDateTimeLocalToISOZ(userEventsToLocal) || undefined,
            pageIndex,
            size: userEventsSize,
          },
        });
        const normalized = normalizeUserEvents(res?.data);
        setUserEvents(normalized.items);
        setUserEventsTotal(normalized.total);
        setUserEventsPageIndex(pageIndex);
      } catch (e: any) {
        setUserEventsError(e?.response?.data?.message || e?.message || "Failed to load user events.");
        setUserEvents([]);
        setUserEventsTotal(null);
      } finally {
        setUserEventsLoading(false);
      }
    },
    [
      userId,
      userEventsPageIndex,
      userEventsSize,
      userEventsFromLocal,
      userEventsToLocal,
    ]
  );

  const loadUserPagesReport = useCallback(async (opts?: { userIdOverride?: string }) => {
    const trimmed = String(opts?.userIdOverride ?? userId).trim();
    if (!trimmed) return;
    setUserPagesLoading(true);
    setUserPagesError(null);
    try {
      const from = fromDateTimeLocalToISOZ(userEventsFromLocal) || undefined;
      const to = fromDateTimeLocalToISOZ(userEventsToLocal) || undefined;

      const maxEvents = 2000;
      const pageSize = Math.min(200, Math.max(10, userEventsSize || 50));
      const counts = new Map<string, number>();
      let pageIndex = 0;
      let seen = 0;

      while (seen < maxEvents) {
        const res = await http.get(`/admin/analytics/users/${encodeURIComponent(trimmed)}/events`, {
          params: {
            from,
            to,
            pageIndex,
            size: pageSize,
          },
        });
        const normalized = normalizeUserEvents(res?.data);
        const items = normalized.items || [];
        if (items.length === 0) break;

        for (const ev of items) {
          const label = String(ev?.page || "").trim() || t("common.unknown", { defaultValue: "Onbekend" });
          counts.set(label, (counts.get(label) || 0) + 1);
        }

        seen += items.length;
        if (items.length < pageSize) break;
        pageIndex += 1;
      }

      const sorted = Array.from(counts.entries())
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => b.count - a.count);
      setUserPagesCounts(sorted);
    } catch (e: any) {
      setUserPagesError(e?.response?.data?.message || e?.message || "Failed to generate report.");
      setUserPagesCounts([]);
    } finally {
      setUserPagesLoading(false);
    }
  }, [
    t,
    userId,
    userEventsFromLocal,
    userEventsSize,
    userEventsToLocal,
  ]);

  const loadUserDailyReport = useCallback(async (opts?: { userIdOverride?: string }) => {
    const trimmed = String(opts?.userIdOverride ?? userId).trim();
    if (!trimmed) return;
    setUserDailyLoading(true);
    setUserDailyError(null);
    setUserDailySummary(null);
    try {
      const fromDay = fromDate;
      const toDay = toDate;

      const [dailyRes, summaryRes] = await Promise.allSettled([
        http.get(`/admin/analytics/logins/users/${encodeURIComponent(trimmed)}/daily`, {
          params: { fromDay, toDay },
        }),
        http.get(`/admin/analytics/logins/users/${encodeURIComponent(trimmed)}/summary`, {
          params: { fromDay, toDay },
        }),
      ]);

      if (dailyRes.status === "fulfilled") {
        const normalized = normalizeDaily(dailyRes.value?.data);
        setUserDailyCounts(
          normalized
            .map((it) => ({ day: it.date, count: it.count }))
            .sort((a, b) => a.day.localeCompare(b.day))
        );
      } else {
        const e: any = dailyRes.reason;
        throw e;
      }

      if (summaryRes.status === "fulfilled") {
        const raw = summaryRes.value?.data as UserDailyLoginSummary;
        setUserDailySummary(raw || null);
      } else {
        setUserDailySummary(null);
      }
    } catch (e: any) {
      setUserDailyError(e?.response?.data?.message || e?.message || "Failed to generate daily report.");
      setUserDailyCounts([]);
      setUserDailySummary(null);
    } finally {
      setUserDailyLoading(false);
    }
  }, [fromDate, toDate, userId]);

  const loadUserDailySplitReport = useCallback(
    async (opts?: { userIdOverride?: string; groupByOverride?: "page" | "feature" | "path" }) => {
      const trimmed = String(opts?.userIdOverride ?? userId).trim();
      if (!trimmed) return;
      if (!params) return;
      const groupBy = opts?.groupByOverride ?? userDailySplitGroupBy;
      setUserDailySplitLoading(true);
      setUserDailySplitError(null);
      try {
        if (groupBy === "page") {
          const res = await http.get(`/admin/analytics/usage/users/${encodeURIComponent(trimmed)}/pages/daily`, {
            params: { fromDay: params.fromDay, toDay: params.toDay },
          });

          const raw = res?.data;
          const arr = (Array.isArray(raw) ? raw : raw?.items || raw?.entries || raw?.data) as UserPageDailyUsageItem[];
          const items = Array.isArray(arr) ? arr : [];

          const byDay = new Map<string, Map<string, number>>();
          const durationsByDay: Record<string, Record<string, number>> = {};
          const totalsByKey = new Map<string, number>();

          for (const it of items) {
            const day = String(it?.day ?? it?.date ?? it?.label ?? "").trim();
            if (!day) continue;
            const key = String(it?.page ?? it?.key ?? "").trim() || t("common.unknown", { defaultValue: "Onbekend" });
            const count = Number(it?.events ?? it?.requests ?? it?.count ?? it?.hits ?? it?.total ?? 0);
            const safeCount = Number.isFinite(count) ? count : 0;
            const dur = Number(it?.totalDurationMs ?? it?.durationMs ?? 0);
            const safeDur = Number.isFinite(dur) ? dur : 0;

            if (!byDay.has(day)) byDay.set(day, new Map());
            const m = byDay.get(day)!;
            m.set(key, (m.get(key) || 0) + safeCount);
            totalsByKey.set(key, (totalsByKey.get(key) || 0) + safeCount);

            if (!durationsByDay[day]) durationsByDay[day] = {};
            durationsByDay[day][key] = (durationsByDay[day][key] || 0) + safeDur;
          }

          const topN = 5;
          const sortedKeys = Array.from(totalsByKey.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([key]) => key);
          const topKeys = sortedKeys.slice(0, topN);

          const baseDays = (userDailyCounts.length ? userDailyCounts.map((d) => d.day) : Array.from(byDay.keys())).sort(
            (a, b) => String(a).localeCompare(String(b))
          );

          const chartData = baseDays.map((day) => {
            const row: any = { day };
            for (const k of topKeys) row[k] = 0;
            row.__other__ = 0;
            const m = byDay.get(day);
            if (m) {
              for (const [k, v] of Array.from(m.entries())) {
                if (topKeys.includes(k)) row[k] = (row[k] || 0) + v;
                else row.__other__ += v;
              }
            }
            return row;
          });

          const durationsFiltered: Record<string, Record<string, number>> = {};
          for (const day of baseDays) {
            const src = durationsByDay[day] || {};
            const out: Record<string, number> = {};
            let other = 0;
            for (const [k, v] of Object.entries(src)) {
              if (topKeys.includes(k)) out[k] = (out[k] || 0) + (v || 0);
              else other += v || 0;
            }
            if (other > 0) out.__other__ = other;
            durationsFiltered[day] = out;
          }

          setUserDailySplitDurationsByDay(durationsFiltered);
          setUserDailySplitSeriesKeys([...topKeys, "__other__"]);
          setUserDailySplitChartData(chartData);
          setUserDailySplitLoadedKey(`${trimmed}::${groupBy}::${params.fromDay}::${params.toDay}`);
          return;
        }

        const maxEvents = 2000;
        const pageSize = 200;
        let pageIndex = 0;
        let seen = 0;

        const byDay = new Map<string, Map<string, number>>();
        const totalsByKey = new Map<string, number>();
        setUserDailySplitDurationsByDay({});

        while (seen < maxEvents) {
          const size = Math.min(pageSize, maxEvents - seen);
          const res = await http.get(`/admin/analytics/users/${encodeURIComponent(trimmed)}/events`, {
            params: {
              from: params.from,
              to: params.to,
              pageIndex,
              size,
            },
          });
          const normalized = normalizeUserEvents(res?.data);
          const items = normalized.items || [];
          if (items.length === 0) break;

          for (const ev of items) {
            const d = getEventDate(ev);
            if (!d) continue;
            const day = dayKeyUTC(d);
            const rawKey =
              groupBy === "feature" ? (ev as any)?.feature : groupBy === "path" ? (ev as any)?.path : (ev as any)?.page;
            const key = String(rawKey || "").trim() || t("common.unknown", { defaultValue: "Onbekend" });
            if (!byDay.has(day)) byDay.set(day, new Map());
            const m = byDay.get(day)!;
            m.set(key, (m.get(key) || 0) + 1);
            totalsByKey.set(key, (totalsByKey.get(key) || 0) + 1);
          }

          seen += items.length;
          if (items.length < size) break;
          pageIndex += 1;
        }

        const topN = 5;
        const sortedKeys = Array.from(totalsByKey.entries())
          .sort((a, b) => b[1] - a[1])
          .map(([key]) => key);
        const topKeys = sortedKeys.slice(0, topN);

        const baseDays = (userDailyCounts.length ? userDailyCounts.map((d) => d.day) : Array.from(byDay.keys())).sort(
          (a, b) => String(a).localeCompare(String(b))
        );

        const chartData = baseDays.map((day) => {
          const row: any = { day };
          for (const k of topKeys) row[k] = 0;
          row.__other__ = 0;
          const m = byDay.get(day);
          if (m) {
            for (const [k, v] of Array.from(m.entries())) {
              if (topKeys.includes(k)) row[k] = (row[k] || 0) + v;
              else row.__other__ += v;
            }
          }
          return row;
        });

        setUserDailySplitSeriesKeys([...topKeys, "__other__"]);
        setUserDailySplitChartData(chartData);
        setUserDailySplitLoadedKey(`${trimmed}::${groupBy}::${params.from}::${params.to}`);
      } catch (e: any) {
        setUserDailySplitError(e?.response?.data?.message || e?.message || "Failed to split daily chart.");
        setUserDailySplitSeriesKeys([]);
        setUserDailySplitChartData([]);
        setUserDailySplitLoadedKey(null);
      } finally {
        setUserDailySplitLoading(false);
      }
    },
    [params, t, userDailyCounts, userDailySplitGroupBy, userId]
  );

  useEffect(() => {
    if (!userDailySplitMode) return;
    const trimmed = String(userId || "").trim();
    if (!trimmed) return;
    if (!params) return;
    const rangeKey =
      userDailySplitGroupBy === "page" ? `${params.fromDay}::${params.toDay}` : `${params.from}::${params.to}`;
    const wantKey = `${trimmed}::${userDailySplitGroupBy}::${rangeKey}`;
    if (userDailySplitLoading) return;
    if (userDailySplitLoadedKey === wantKey && userDailySplitChartData.length > 0) return;
    loadUserDailySplitReport({ userIdOverride: trimmed, groupByOverride: userDailySplitGroupBy });
  }, [
    loadUserDailySplitReport,
    params,
    userDailySplitChartData.length,
    userDailySplitGroupBy,
    userDailySplitLoadedKey,
    userDailySplitLoading,
    userDailySplitMode,
    userId,
  ]);

  const userPagesPieData = useMemo(() => {
    const maxSlices = 8;
    const total = userPagesCounts.reduce((sum, it) => sum + (it.count || 0), 0);
    if (!total) return [];
    const top = userPagesCounts.slice(0, maxSlices);
    const restCount = userPagesCounts.slice(maxSlices).reduce((sum, it) => sum + (it.count || 0), 0);
    const out = top.map((it) => ({ name: it.label, value: it.count }));
    if (restCount > 0) out.push({ name: t("common.other", { defaultValue: "Overig" }), value: restCount });
    return out;
  }, [t, userPagesCounts]);

  const [userPagesActiveIndex, setUserPagesActiveIndex] = useState<number | null>(null);

  const userDailySplitHasNonZero = useMemo(() => {
    if (!userDailySplitMode) return false;
    if (!userDailySplitChartData.length) return false;
    if (!userDailySplitSeriesKeys.length) return false;
    for (const k of userDailySplitSeriesKeys) {
      for (const row of userDailySplitChartData) {
        if ((row?.[k] || 0) > 0) return true;
      }
    }
    return false;
  }, [userDailySplitChartData, userDailySplitMode, userDailySplitSeriesKeys]);

  const userDailyChartData = useMemo(() => {
    if (userDailySplitMode && userDailySplitHasNonZero) return userDailySplitChartData;
    return userDailyCounts;
  }, [userDailyCounts, userDailySplitChartData, userDailySplitHasNonZero, userDailySplitMode]);

  const usageTimeseriesChartData = useMemo(() => {
    const days = usageTimeseries?.days;
    const series = usageTimeseries?.series;
    if (!Array.isArray(days) || !Array.isArray(series) || days.length === 0) return [];

    const safeSeries = series
      .map((s) => ({ key: String(s?.key ?? "").trim(), counts: Array.isArray(s?.counts) ? s.counts : [] }))
      .filter((s) => s.key);

    return days.map((day, idx) => {
      const row: Record<string, any> = { day };
      let total = 0;
      for (const s of safeSeries) {
        const value = Number(s.counts[idx] ?? 0);
        const count = Number.isFinite(value) ? value : 0;
        row[s.key] = count;
        total += count;
      }
      row.__total__ = total;
      return row;
    });
  }, [usageTimeseries]);

  const usageTimeseriesKeys = useMemo(() => {
    const series = usageTimeseries?.series;
    if (!Array.isArray(series)) return [];
    return series
      .map((s) => String(s?.key ?? "").trim())
      .filter(Boolean);
  }, [usageTimeseries]);

  const resolveBrushIndices = useCallback(
    (leftDay?: string, rightDay?: string) => {
      if (!leftDay || !rightDay) return null;
      const days = usageTimeseriesChartData.map((d: any) => String(d.day));
      const leftIndex = days.indexOf(String(leftDay));
      const rightIndex = days.indexOf(String(rightDay));
      if (leftIndex < 0 || rightIndex < 0) return null;
      const startIndex = Math.min(leftIndex, rightIndex);
      const endIndex = Math.max(leftIndex, rightIndex);
      if (startIndex === endIndex) return null;
      return { startIndex, endIndex };
    },
    [usageTimeseriesChartData]
  );

  useEffect(() => {
    timeseriesSelectingRef.current = timeseriesSelecting;
  }, [timeseriesSelecting]);

  useEffect(() => {
    if (!timeseriesSelecting?.left) return;
    const onWindowMouseUp = () => {
      const current = timeseriesSelectingRef.current;
      const left = current?.left;
      const right = current?.right || timeseriesLastActiveLabelRef.current || undefined;
      if (!left || !right) {
        setTimeseriesSelecting(null);
        return;
      }
      const next = resolveBrushIndices(left, right);
      if (next) setTimeseriesBrush(next);
      setTimeseriesSelecting(null);
    };
    window.addEventListener("mouseup", onWindowMouseUp);
    return () => window.removeEventListener("mouseup", onWindowMouseUp);
  }, [resolveBrushIndices, timeseriesSelecting?.left]);

  useEffect(() => {
    // Reset zoom when series or date range changes
    const len = usageTimeseriesChartData.length;
    if (len <= 0) {
      setTimeseriesBrush(null);
      return;
    }
    setTimeseriesBrush({ startIndex: Math.max(0, len - 14), endIndex: len - 1 });
  }, [usageTimeseriesChartData, timeseriesGroupBy]);

  const timeseriesLegendItems = useMemo(() => {
    const keys = ["__total__", ...usageTimeseriesKeys];
    return keys.map((key, idx) => {
      const isTotal = key === "__total__";
      const name =
        key === "__total__"
          ? t("reports.analytics.total", { defaultValue: "Totaal" })
          : key === "__other__"
            ? t("common.other", { defaultValue: "Overig" })
            : key;
      const color = isTotal
        ? "#94a3b8"
        : [
            "#4f46e5",
            "#06b6d4",
            "#22c55e",
            "#f59e0b",
            "#ef4444",
            "#a855f7",
            "#14b8a6",
            "#64748b",
          ][(idx - 1 + 8) % 8];
      return { key, name, color };
    });
  }, [t, usageTimeseriesKeys]);

  const toggleTimeseriesKey = useCallback((key: string) => {
    setTimeseriesSelectedKey((prev) => (prev === key ? null : key));
  }, []);

  const pickUserFromTopList = useCallback(
    async (pickedUserId: string) => {
      setUserId(pickedUserId);
      setTopUsersDrawerUserId(pickedUserId);
      setTopUsersDrawerOpen(true);
      setUserPagesReportPlacement("topUsersDrawer");
      setUserDailyReportPlacement("topUsersDrawer");
      setUserEventsPageIndex(0);
      setUserDailySplitMode(true);
      setUserDailySplitGroupBy("page");
      setUserDailySplitSelectedKey(null);
      // load events + report (non-blocking)
      try {
        await loadUserEvents({ pageIndex: 0, userIdOverride: pickedUserId });
      } catch {
        // ignore
      }
      try {
        await loadUserPagesReport({ userIdOverride: pickedUserId });
      } catch {
        // ignore
      }
      try {
        await loadUserDailyReport({ userIdOverride: pickedUserId });
      } catch {
        // ignore
      }
      try {
        await loadUserDailySplitReport({ userIdOverride: pickedUserId, groupByOverride: "page" });
      } catch {
        // ignore
      }
    },
    [loadUserDailyReport, loadUserDailySplitReport, loadUserEvents, loadUserPagesReport]
  );

  return (
    <PageTemplate
      title={t("reports.analytics.title", { defaultValue: "Analytics" })}
      wrapperClassName="max-w-6xl"
    >
      <div className="space-y-6">
        <div className="lumen-plain-card rounded-2xl p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-indigo-700 dark:text-indigo-200">
                <span className="inline-flex items-center gap-2">
                  <span className="text-indigo-600 dark:text-indigo-300">
                    <FaChartLine size={22} />
                  </span>
                  {t("reports.analytics.subtitle", { defaultValue: "Gebruik en activiteit" })}
                </span>
              </h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                {t("reports.analytics.hint", {
                  defaultValue: "Top pagina’s/features en dagelijkse activiteit (geauthenticeerde API-requests).",
                })}
              </p>
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[140px]">
                <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <span className="inline-flex items-center gap-2">
                    <FaRegCalendarAlt />
                    {t("common.from", { defaultValue: "Van" })}
                  </span>
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-indigo-100/70 bg-white/80 px-3 py-2 text-sm text-gray-900 shadow-sm dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-gray-100"
                />
              </div>
              <div className="min-w-[140px]">
                <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {t("common.to", { defaultValue: "Tot" })}
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-indigo-100/70 bg-white/80 px-3 py-2 text-sm text-gray-900 shadow-sm dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-gray-100"
                />
              </div>
              <button
                type="button"
                onClick={load}
                disabled={loading || !params}
                className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading
                  ? t("common.loading", { defaultValue: "Bezig..." })
                  : t("common.refresh", { defaultValue: "Verversen" })}
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-4 rounded-2xl border border-red-200/80 bg-red-50/90 px-4 py-3 text-sm text-red-800 shadow-sm dark:border-red-500/40 dark:bg-red-900/40 dark:text-red-100">
              {error}
            </div>
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lumen-plain-card rounded-2xl p-6">
            <h3 className="text-sm font-semibold text-indigo-700 dark:text-indigo-200">
              <span className="inline-flex items-center gap-2">
                <FaRoute />
                {t("reports.analytics.top_paths", { defaultValue: "Top paths" })}
              </span>
            </h3>
            <ol className="mt-3 space-y-2 text-sm">
              {topPaths.length === 0 ? (
                <li className="text-gray-500 dark:text-gray-400">—</li>
              ) : (
                topPaths.slice(0, limit).map((it, idx) => (
                  <li key={`${it.label}-${idx}`} className="flex items-start justify-between gap-3">
                    <span className="min-w-0 break-words text-gray-800 dark:text-gray-100">
                      {idx + 1}. {it.label}
                    </span>
                    <span className="shrink-0 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200">
                      {it.count}
                    </span>
                  </li>
                ))
              )}
            </ol>
          </div>

          <div className="lumen-plain-card rounded-2xl p-6">
            <h3 className="text-sm font-semibold text-indigo-700 dark:text-indigo-200">
              <span className="inline-flex items-center gap-2">
                <FaListOl />
                {t("reports.analytics.top_pages", { defaultValue: "Top pagina’s" })}
              </span>
            </h3>
            <ol className="mt-3 space-y-2 text-sm">
              {topPages.length === 0 ? (
                <li className="text-gray-500 dark:text-gray-400">—</li>
              ) : (
                topPages.slice(0, limit).map((it, idx) => (
                  <li key={`${it.label}-${idx}`} className="flex items-start justify-between gap-3">
                    <span className="min-w-0 break-words text-gray-800 dark:text-gray-100">
                      {idx + 1}. {it.label}
                    </span>
                    <span className="shrink-0 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200">
                      {it.count}
                    </span>
                  </li>
                ))
              )}
            </ol>
          </div>

          <div className="lumen-plain-card rounded-2xl p-6">
            <h3 className="text-sm font-semibold text-indigo-700 dark:text-indigo-200">
              <span className="inline-flex items-center gap-2">
                <FaListOl />
                {t("reports.analytics.top_features", { defaultValue: "Top features" })}
              </span>
            </h3>
            <ol className="mt-3 space-y-2 text-sm">
              {topFeatures.length === 0 ? (
                <li className="text-gray-500 dark:text-gray-400">—</li>
              ) : (
                topFeatures.slice(0, limit).map((it, idx) => (
                  <li key={`${it.label}-${idx}`} className="flex items-start justify-between gap-3">
                    <span className="min-w-0 break-words text-gray-800 dark:text-gray-100">
                      {idx + 1}. {it.label}
                    </span>
                    <span className="shrink-0 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200">
                      {it.count}
                    </span>
                  </li>
                ))
              )}
            </ol>
          </div>
        </div>

        <div className="lumen-plain-card rounded-2xl p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-indigo-700 dark:text-indigo-200">
                {t("reports.analytics.top_users", { defaultValue: "Top users" })}
              </h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t("reports.analytics.top_users_hint", {
                  defaultValue: "Gebaseerd op usage events en (geschatte) sessies.",
                })}
              </p>
            </div>
            <div className="min-w-[180px]">
              <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {t("reports.analytics.idle_minutes", { defaultValue: "Idle minutes" })}
              </label>
              <input
                type="number"
                min={5}
                max={240}
                value={idleMinutes}
                onChange={(e) => setIdleMinutes(Number(e.target.value || 30))}
                className="mt-1 w-full rounded-xl border border-indigo-100/70 bg-white/80 px-3 py-2 text-sm text-gray-900 shadow-sm dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-gray-100"
              />
            </div>
          </div>

          {topUsersError && (
            <div className="mt-4 rounded-2xl border border-amber-200/80 bg-amber-50/90 px-4 py-3 text-sm text-amber-900 shadow-sm dark:border-amber-500/40 dark:bg-amber-900/20 dark:text-amber-100">
              {topUsersError}
            </div>
          )}

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="bg-indigo-600/90 text-xs font-semibold uppercase tracking-wide text-white">
                  <th className="rounded-l-xl py-2 pl-4 pr-4">{t("common.user", { defaultValue: "User" })}</th>
                  <th className="py-2 pr-4">{t("reports.analytics.events", { defaultValue: "Events" })}</th>
                  <th className="py-2 pr-4">{t("reports.analytics.sessions", { defaultValue: "Sessies" })}</th>
                  <th
                    className="py-2 pr-4"
                    title={t("reports.analytics.duration_format_hint", { defaultValue: "Duur als hh:mm:ss" })}
                  >
                    {t("reports.analytics.total_session", { defaultValue: "Totaal" })}{" "}
                    <span className="opacity-90">(hh:mm:ss)</span>
                  </th>
                  <th
                    className="py-2 pr-4"
                    title={t("reports.analytics.duration_format_hint", { defaultValue: "Duur als hh:mm:ss" })}
                  >
                    {t("reports.analytics.avg_session", { defaultValue: "Gemiddeld" })}{" "}
                    <span className="opacity-90">(hh:mm:ss)</span>
                  </th>
                  <th className="rounded-r-xl py-2 pr-4">{t("reports.analytics.last_seen", { defaultValue: "Laatste" })}</th>
                </tr>
              </thead>
              <tbody className="text-gray-800 dark:text-gray-100">
                {topUsers?.length ? (
                  topUsers.slice(0, 5).map((u, idx) => {
                    const id = String(u?.userId || "").trim();
                    const label = (id && userLabelById.get(id)) || id || "—";
                    const isSelected = Boolean(id) && id === userId;
                    return (
                      <tr
                        key={`${id}-${idx}`}
                        role="button"
                        aria-selected={isSelected}
                        tabIndex={0}
                        onClick={() => id && pickUserFromTopList(id)}
                        onKeyDown={(e) => {
                          if (!id) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            pickUserFromTopList(id);
                          }
                        }}
                        className={[
                          "cursor-pointer border-t border-indigo-50/60 transition focus:outline-none focus:ring-2 focus:ring-indigo-300/70 dark:border-indigo-500/20",
                          isSelected
                            ? "bg-indigo-50/70 hover:bg-indigo-50/90 dark:bg-indigo-900/20 dark:hover:bg-indigo-900/30"
                            : "hover:bg-indigo-50/40 dark:hover:bg-indigo-900/15",
                        ].join(" ")}
                      >
                        <td className="py-2 pl-4 pr-4">
                          <div className="text-left font-semibold text-indigo-700 dark:text-indigo-200">{label}</div>
                          {label !== id && (
                            <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{id}</div>
                          )}
                        </td>
                        <td className="py-2 pr-4 whitespace-nowrap">{typeof u?.events === "number" ? u.events : "—"}</td>
                        <td className="py-2 pr-4 whitespace-nowrap">{typeof u?.sessions === "number" ? u.sessions : "—"}</td>
                        <td className="py-2 pr-4 whitespace-nowrap">{formatDurationSeconds(u?.totalSessionSeconds ?? null)}</td>
                        <td className="py-2 pr-4 whitespace-nowrap">{formatDurationSeconds(u?.avgSessionSeconds ?? null)}</td>
                        <td className="py-2 pr-2 whitespace-nowrap">{formatDateTimeNl(u?.lastSeen)}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td className="py-3 text-gray-500 dark:text-gray-400" colSpan={6}>
                      —
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {topUsersDrawerOpen && userPagesReportPlacement === "topUsersDrawer" && topUsersDrawerUserId === userId && (
            <div className="mt-5 rounded-2xl border border-indigo-100/60 bg-white/70 p-4 shadow-sm dark:border-indigo-500/30 dark:bg-gray-900/40">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-indigo-700 dark:text-indigo-200">
                    {t("reports.analytics.user_pages_title", { defaultValue: "Top pagina’s (user)" })}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    {(userId && userLabelById.get(userId)) || userId}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setTopUsersDrawerOpen(false);
                    setTopUsersDrawerUserId(null);
                    setUserPagesReportPlacement(null);
                    setUserDailyReportPlacement(null);
                  }}
                  className="rounded-xl border border-indigo-100/70 bg-white/80 px-3 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:bg-white dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-indigo-200"
                >
                  {t("common.close", { defaultValue: "Sluiten" })}
                </button>
              </div>

              {userPagesLoading ? (
                <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                  {t("common.loading", { defaultValue: "Bezig..." })}
                </div>
              ) : userPagesCounts.length === 0 ? (
                <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">—</div>
              ) : (
                <div className="mt-4 grid gap-6 lg:grid-cols-2">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t("reports.analytics.user_pages_hint", {
                        defaultValue: "Verdeling op basis van X-Lumen-Page (sampling tot 2000 events).",
                      })}
                    </p>
                    {userPagesError && (
                      <div className="mt-3 rounded-2xl border border-red-200/80 bg-red-50/90 px-4 py-3 text-sm text-red-800 shadow-sm dark:border-red-500/40 dark:bg-red-900/40 dark:text-red-100">
                        {userPagesError}
                      </div>
                    )}
                    <div className="relative mt-4 h-[260px] overflow-visible">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Tooltip allowEscapeViewBox={{ x: false, y: false }} wrapperStyle={{ zIndex: 50 }} />
                          <Legend />
                          <Pie
                            data={userPagesPieData}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={55}
                            outerRadius={90}
                            paddingAngle={2}
                            activeIndex={userPagesActiveIndex ?? undefined}
                            onMouseEnter={(_, idx) => setUserPagesActiveIndex(idx)}
                            onMouseLeave={() => setUserPagesActiveIndex(null)}
                            activeShape={(props: any) => {
                              const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
                              return (
                                <Sector
                                  cx={cx}
                                  cy={cy}
                                  innerRadius={innerRadius}
                                  outerRadius={outerRadius + 10}
                                  startAngle={startAngle}
                                  endAngle={endAngle}
                                  fill={fill}
                                />
                              );
                            }}
                          >
                            {userPagesPieData.map((_, idx) => (
                              <Cell
                                key={idx}
                                fill={[
                                  "#4f46e5",
                                  "#06b6d4",
                                  "#22c55e",
                                  "#f59e0b",
                                  "#ef4444",
                                  "#a855f7",
                                  "#14b8a6",
                                  "#94a3b8",
                                  "#64748b",
                                ][idx % 9]}
                              />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-indigo-700 dark:text-indigo-200">
                      {t("reports.analytics.user_pages_list_title", { defaultValue: "Top lijst" })}
                    </div>
                    <ol className="mt-3 space-y-2 text-sm">
                      {userPagesCounts.slice(0, 12).map((it, idx) => (
                        <li key={`${it.label}-${idx}`} className="flex items-start justify-between gap-3">
                          <span className="min-w-0 break-words text-gray-800 dark:text-gray-100">
                            {idx + 1}. {it.label}
                          </span>
                          <span className="shrink-0 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200">
                            {it.count}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              )}

              {userDailyReportPlacement === "topUsersDrawer" && (
                <div className="mt-6 border-t border-indigo-100/60 pt-5 dark:border-indigo-500/20">
                  <div className="text-sm font-semibold text-indigo-700 dark:text-indigo-200">
                    {t("reports.analytics.user_daily_title", { defaultValue: "Activiteit per dag (user)" })}
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t("reports.analytics.user_daily_hint", {
                      defaultValue:
                        "Aantal geauthenticeerde API-requests per dag voor deze user (geen Keycloak login-events).",
                    })}
                  </p>

                  {userDailyError && (
                    <div className="mt-3 rounded-2xl border border-red-200/80 bg-red-50/90 px-4 py-3 text-sm text-red-800 shadow-sm dark:border-red-500/40 dark:bg-red-900/40 dark:text-red-100">
                      {userDailyError}
                    </div>
                  )}

                  {userDailyLoading ? (
                    <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                      {t("common.loading", { defaultValue: "Bezig..." })}
                    </div>
                  ) : userDailyCounts.length === 0 ? (
                    <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">—</div>
                  ) : (
                    <>
                      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          {t("reports.analytics.group_by", { defaultValue: "Groeperen op" })}:{" "}
                          <span className="text-indigo-700 dark:text-indigo-200">page</span>
                        </div>
                        {userDailySplitLoading && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {t("common.loading", { defaultValue: "Bezig..." })}
                          </div>
                        )}
                      </div>

                      {userDailySplitError && (
                        <div className="mt-3 rounded-2xl border border-amber-200/80 bg-amber-50/90 px-4 py-3 text-sm text-amber-900 shadow-sm dark:border-amber-500/40 dark:bg-amber-900/20 dark:text-amber-100">
                          {userDailySplitError}
                        </div>
                      )}

                      <div className="mt-3 flex flex-wrap gap-4 text-xs text-gray-600 dark:text-gray-300">
                        <div>
                          {t("reports.analytics.user_daily_days", { defaultValue: "Actieve dagen" })}:{" "}
                          <span className="font-semibold text-gray-900 dark:text-gray-100">
                            {typeof userDailySummary?.days === "number" ? userDailySummary.days : userDailyCounts.length}
                          </span>
                        </div>
                        <div>
                          {t("reports.analytics.user_daily_events", { defaultValue: "API-requests" })}:{" "}
                          <span className="font-semibold text-gray-900 dark:text-gray-100">
                            {typeof userDailySummary?.total === "number"
                              ? userDailySummary.total
                              : userDailyCounts.reduce((s, it) => s + (it.count || 0), 0)}
                          </span>
                        </div>
                        {(userDailySummary?.firstDay || userDailySummary?.firstDate) && (
                          <div>
                            {t("reports.analytics.user_daily_first", { defaultValue: "Eerste dag" })}:{" "}
                            <span className="font-semibold text-gray-900 dark:text-gray-100">
                              {String(userDailySummary.firstDay || userDailySummary.firstDate)}
                            </span>
                          </div>
                        )}
                        {(userDailySummary?.lastDay || userDailySummary?.lastDate) && (
                          <div>
                            {t("reports.analytics.user_daily_last", { defaultValue: "Laatste dag" })}:{" "}
                            <span className="font-semibold text-gray-900 dark:text-gray-100">
                              {String(userDailySummary.lastDay || userDailySummary.lastDate)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="mt-4 h-[220px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={userDailyChartData}
                            margin={{ top: 8, right: 12, left: 0, bottom: 8 }}
                            barCategoryGap={userDailySplitMode && userDailySplitHasNonZero ? 2 : 14}
                            barGap={0}
                            maxBarSize={userDailySplitMode && userDailySplitHasNonZero ? 22 : 12}
                          >
                            <defs>
                              <linearGradient id="lumenBarIndigoUserDaily" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#4f46e5" stopOpacity={0.95} />
                                <stop offset="100%" stopColor="#4338ca" stopOpacity={0.95} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="2 6" opacity={0.18} />
                            <XAxis
                              dataKey="day"
                              tick={{ fontSize: 12 }}
                              tickFormatter={(v) => formatShortNl(v)}
                              tickLine={false}
                              axisLine={false}
                              tickMargin={10}
                              minTickGap={18}
                            />
                            <YAxis tick={{ fontSize: 12 }} allowDecimals={false} tickLine={false} axisLine={false} />
                            <Tooltip
                              cursor={{ fill: "rgba(79,70,229,0.06)" }}
                              content={
                                userDailySplitMode && userDailySplitHasNonZero
                                  ? ({ active, label, payload }: any) => {
                                      if (!active || !payload || !Array.isArray(payload)) return null;
                                      const day = String(label ?? "").trim();
                                      const items = payload
                                        .filter((p: any) => p && p.dataKey && p.dataKey !== "day")
                                        .map((p: any) => ({
                                          key: String(p.dataKey),
                                          name:
                                            String(p.dataKey) === "__other__"
                                              ? t("common.other", { defaultValue: "Overig" })
                                              : String(p.dataKey),
                                          value: Number(p.value ?? 0),
                                          color: p.color || p.fill,
                                        }))
                                        .filter((it: any) => Number.isFinite(it.value) && it.value > 0)
                                        .sort((a: any, b: any) => b.value - a.value);
                                      if (items.length === 0) return null;
                                      return (
                                        <div className="rounded-xl border border-gray-200 bg-white/95 px-3 py-2 text-xs shadow-lg dark:border-gray-700 dark:bg-gray-900/95">
                                          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                            {formatShortNl(String(label))}
                                          </div>
                                          <div className="space-y-1">
                                            {items.map((it: any) => {
                                              const isFocused = userDailySplitSelectedKey === it.key;
                                              const shouldFade = userDailySplitSelectedKey && !isFocused;
                                              const dur =
                                                day && userDailySplitDurationsByDay?.[day]
                                                  ? userDailySplitDurationsByDay[day]?.[it.key]
                                                  : undefined;
                                              return (
                                                <div
                                                  key={it.key}
                                                  className={`flex items-center justify-between gap-3 ${shouldFade ? "opacity-50" : ""}`}
                                                >
                                                  <div className="flex min-w-0 items-center gap-2">
                                                    <span
                                                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                                                      style={{ backgroundColor: it.color || "#94a3b8" }}
                                                    />
                                                    <span className="min-w-0 truncate text-gray-800 dark:text-gray-100">
                                                      {it.name}
                                                    </span>
                                                  </div>
                                                  <div className="shrink-0 text-right">
                                                    <div className="font-semibold text-gray-900 dark:text-gray-100">{it.value}</div>
                                                    {typeof dur === "number" && dur > 0 && (
                                                      <div className="text-[11px] text-gray-500 dark:text-gray-400">
                                                        {formatDurationMsCompact(dur)}
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      );
                                    }
                                  : undefined
                              }
                              labelFormatter={(label: any) => formatShortNl(String(label))}
                              formatter={(value: any) => value}
                            />
                            {userDailySplitMode && userDailySplitHasNonZero ? (
                              <>
                                {userDailySplitSeriesKeys.map((k, idx) => (
                                  (() => {
                                    const isFocused = userDailySplitSelectedKey === k;
                                    const shouldFade = userDailySplitSelectedKey && !isFocused;
                                    const color =
                                      k === "__other__"
                                        ? "#94a3b8"
                                        : ["#4f46e5", "#06b6d4", "#22c55e", "#f59e0b", "#ef4444", "#a855f7"][idx % 6];
                                    return (
                                  <Bar
                                    key={k}
                                    dataKey={k}
                                    stackId="u"
                                    name={k === "__other__" ? t("common.other", { defaultValue: "Overig" }) : k}
                                    fill={color}
                                    radius={0}
                                    stroke="none"
                                    opacity={shouldFade ? 0.18 : 0.95}
                                    isAnimationActive={false}
                                  />
                                    );
                                  })()
                                ))}
                              </>
                            ) : (
                              <Bar
                                dataKey="count"
                                fill="url(#lumenBarIndigoUserDaily)"
                                radius={0}
                                stroke="none"
                                isAnimationActive={false}
                              />
                            )}
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      {userDailySplitMode && userDailySplitHasNonZero && userDailySplitChartData.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-2 text-xs">
                          {userDailySplitSeriesKeys.map((k, idx) => {
                            const name = k === "__other__" ? t("common.other", { defaultValue: "Overig" }) : k;
                            const color =
                              k === "__other__"
                                ? "#94a3b8"
                                : ["#4f46e5", "#06b6d4", "#22c55e", "#f59e0b", "#ef4444", "#a855f7"][idx % 6];
                            const isSelected = userDailySplitSelectedKey === k;
                            const shouldFade = userDailySplitSelectedKey && !isSelected;
                            return (
                              <button
                                key={k}
                                type="button"
                                onClick={() => setUserDailySplitSelectedKey((prev) => (prev === k ? null : k))}
                                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 transition ${
                                  isSelected
                                    ? "border-indigo-300 bg-indigo-50 text-indigo-800 dark:border-indigo-500/40 dark:bg-indigo-900/25 dark:text-indigo-100"
                                    : "border-gray-200 bg-white/70 text-gray-700 hover:bg-white dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200"
                                } ${shouldFade ? "opacity-60" : ""}`}
                                title={
                                  isSelected
                                    ? t("reports.analytics.legend_unfocus", { defaultValue: "Klik om focus uit te zetten" })
                                    : t("reports.analytics.legend_focus", { defaultValue: "Klik om te focussen" })
                                }
                              >
                                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
                                <span className="max-w-[240px] truncate">{name}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {userDailySplitMode && !userDailySplitLoading && userDailySplitChartData.length > 0 && !userDailySplitHasNonZero && (
                        <div className="mt-3 rounded-2xl border border-amber-200/80 bg-amber-50/90 px-4 py-3 text-xs text-amber-900 shadow-sm dark:border-amber-500/40 dark:bg-amber-900/20 dark:text-amber-100">
                          {t("reports.analytics.user_daily_split_empty", {
                            defaultValue:
                              "Geen page-activiteit in deze periode (of response kon niet worden gemapt). Totaal-weergave wordt getoond.",
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="lumen-plain-card rounded-2xl p-6">
          <h3 className="text-sm font-semibold text-indigo-700 dark:text-indigo-200">
            {t("reports.analytics.daily_logins", { defaultValue: "Dagelijkse activiteit" })}
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t("reports.analytics.daily_logins_hint", {
              defaultValue:
                "Alle gebruikers: geauthenticeerde API-requests per dag (proxy voor activiteit; geen Keycloak login-events).",
            })}
          </p>

          {uniqueUsersDailyError && (
            <div className="mt-4 rounded-2xl border border-amber-200/80 bg-amber-50/90 px-4 py-3 text-sm text-amber-900 shadow-sm dark:border-amber-500/40 dark:bg-amber-900/20 dark:text-amber-100">
              {uniqueUsersDailyError}
            </div>
          )}

          {uniqueUsersDaily.length > 0 && (
            <div className="mt-4 rounded-2xl border border-indigo-100/60 bg-white/70 p-4 shadow-sm dark:border-indigo-500/30 dark:bg-gray-900/40">
              <div className="text-sm font-semibold text-indigo-700 dark:text-indigo-200">
                {t("reports.analytics.unique_users_daily", { defaultValue: "Actieve users per dag" })}
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t("reports.analytics.unique_users_daily_hint", {
                  defaultValue: "Aantal unieke users met activiteit die dag.",
                })}
              </p>
              <div className="mt-4 h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={uniqueUsersDaily.map((it) => ({ day: it.date, count: it.count }))}
                    margin={{ top: 8, right: 12, left: 0, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" opacity={0.18} />
                    <XAxis dataKey="day" tick={{ fontSize: 12 }} tickFormatter={(v) => formatShortNl(v)} />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <Tooltip labelFormatter={(label: any) => formatShortNl(String(label))} formatter={(v: any) => v} />
                    {typeof uniqueUsersDailyMedian === "number" && Number.isFinite(uniqueUsersDailyMedian) && (
                      <ReferenceLine
                        y={uniqueUsersDailyMedian}
                        stroke="#0ea5e9"
                        strokeDasharray="4 6"
                        strokeWidth={2}
                        label={{
                          value: t("reports.analytics.median", { defaultValue: "Mediaan" }) + `: ${Math.round(uniqueUsersDailyMedian)}`,
                          position: "insideTopRight",
                          fill: "#0369a1",
                          fontSize: 12,
                        }}
                      />
                    )}
                    <Bar dataKey="count" fill="#06b6d4" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {t("reports.analytics.timeseries_group_by", { defaultValue: "Groeperen op" })}
            </div>
            <div className="flex gap-2">
              {(["page", "path", "feature"] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setTimeseriesGroupBy(k)}
                  className={`rounded-xl border px-3 py-2 text-sm font-semibold shadow-sm transition ${
                    timeseriesGroupBy === k
                      ? "border-indigo-200 bg-indigo-600 text-white dark:border-indigo-500/40"
                      : "border-indigo-100/70 bg-white/80 text-indigo-700 hover:bg-white dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-indigo-200"
                  }`}
                >
                  {k}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setTimeseriesBrush(null)}
                className="ml-2 rounded-xl border border-indigo-100/70 bg-white/80 px-3 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-white dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-indigo-200"
              >
                {t("common.reset", { defaultValue: "Reset" })}
              </button>
            </div>
          </div>

          {usageTimeseriesError && (
            <div className="mt-4 rounded-2xl border border-amber-200/80 bg-amber-50/90 px-4 py-3 text-sm text-amber-900 shadow-sm dark:border-amber-500/40 dark:bg-amber-900/20 dark:text-amber-100">
              {usageTimeseriesError}
            </div>
          )}

          <div className="mt-4 h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={
                  timeseriesBrush
                    ? usageTimeseriesChartData.slice(
                        timeseriesBrush.startIndex,
                        timeseriesBrush.endIndex + 1
                      )
                    : usageTimeseriesChartData
                }
                margin={{ top: 8, right: 12, left: 0, bottom: 8 }}
                onClick={(state: any) => {
                  // Click anywhere on chart: focus the most dominant non-total series at that x (best effort).
                  const payload = state?.activePayload;
                  if (!Array.isArray(payload) || payload.length === 0) return;
                  const candidates = payload
                    .map((p: any) => ({ key: p?.dataKey, value: Number(p?.value ?? 0) }))
                    .filter((p: any) => p.key && p.key !== "__total__" && Number.isFinite(p.value));
                  if (candidates.length === 0) return;
                  candidates.sort((a: any, b: any) => b.value - a.value);
                  toggleTimeseriesKey(String(candidates[0].key));
                }}
                onMouseDown={(state: any) => {
                  const label = state?.activeLabel;
                  if (!label) return;
                  timeseriesLastActiveLabelRef.current = String(label);
                  setTimeseriesSelecting({ left: String(label), right: String(label) });
                }}
                onMouseMove={(state: any) => {
                  const label = state?.activeLabel;
                  if (label) timeseriesLastActiveLabelRef.current = String(label);
                  if (!timeseriesSelecting?.left) return;
                  if (!label) return;
                  setTimeseriesSelecting((prev) => (prev ? { ...prev, right: String(label) } : prev));
                }}
                onMouseUp={() => {
                  if (!timeseriesSelecting?.left || !timeseriesSelecting?.right) {
                    setTimeseriesSelecting(null);
                    return;
                  }
                  const next = resolveBrushIndices(timeseriesSelecting.left, timeseriesSelecting.right);
                  if (next) setTimeseriesBrush(next);
                  setTimeseriesSelecting(null);
                }}
                onMouseLeave={() => {
                  if (!timeseriesSelectingRef.current?.left) return;
                  const label = timeseriesLastActiveLabelRef.current;
                  if (!label) return;
                  setTimeseriesSelecting((prev) => (prev ? { ...prev, right: String(label) } : prev));
                }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.18} />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(v) => formatShortNl(v)}
                />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip
                  formatter={(value: any) => value}
                  labelFormatter={(label: any) => formatShortNl(String(label))}
                />
                {timeseriesSelecting?.left && timeseriesSelecting?.right && (
                  <ReferenceArea
                    x1={timeseriesSelecting.left}
                    x2={timeseriesSelecting.right}
                    strokeOpacity={0.15}
                    fill="#4f46e5"
                    fillOpacity={0.08}
                  />
                )}
                {usageTimeseriesChartData.length > 10 && timeseriesBrush && (
                  <Brush
                    dataKey="day"
                    height={22}
                    stroke="#4f46e5"
                    travellerWidth={10}
                    startIndex={timeseriesBrush.startIndex}
                    endIndex={timeseriesBrush.endIndex}
                    onChange={(range: any) => {
                      const startIndex = Number(range?.startIndex ?? 0);
                      const endIndex = Number(range?.endIndex ?? usageTimeseriesChartData.length - 1);
                      if (!Number.isFinite(startIndex) || !Number.isFinite(endIndex)) return;
                      setTimeseriesBrush({
                        startIndex: Math.max(0, Math.min(startIndex, usageTimeseriesChartData.length - 1)),
                        endIndex: Math.max(0, Math.min(endIndex, usageTimeseriesChartData.length - 1)),
                      });
                    }}
                    tickFormatter={(v) => formatShortNl(String(v))}
                  />
                )}
                <Legend
                  verticalAlign="bottom"
                  align="left"
                  content={() => (
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2 text-xs">
                      {timeseriesLegendItems.map((it) => {
                        const isSelected = timeseriesSelectedKey === it.key;
                        const shouldFade = timeseriesSelectedKey && !isSelected;
                        return (
                          <button
                            key={it.key}
                            type="button"
                            onClick={() => toggleTimeseriesKey(it.key)}
                            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 transition ${
                              isSelected
                                ? "border-indigo-300 bg-indigo-50 text-indigo-800 dark:border-indigo-500/40 dark:bg-indigo-900/25 dark:text-indigo-100"
                                : "border-gray-200 bg-white/70 text-gray-700 hover:bg-white dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200"
                            } ${shouldFade ? "opacity-60" : ""}`}
                            title={
                              isSelected
                                ? t("reports.analytics.legend_unfocus", { defaultValue: "Klik om focus uit te zetten" })
                                : t("reports.analytics.legend_focus", { defaultValue: "Klik om deze lijn te focussen" })
                            }
                          >
                            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: it.color }} />
                            <span className="max-w-[240px] truncate">{it.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                />
                <Line
                  type="monotone"
                  dataKey="__total__"
                  name={t("reports.analytics.total", { defaultValue: "Totaal" })}
                  stroke="#94a3b8"
                  strokeWidth={timeseriesSelectedKey === "__total__" ? 3 : 2}
                  dot={false}
                  className="cursor-pointer"
                  onClick={() => toggleTimeseriesKey("__total__")}
                  animationDuration={350}
                  animationEasing="ease-out"
                  opacity={
                    timeseriesSelectedKey && timeseriesSelectedKey !== "__total__" ? 0.35 : 1
                  }
                />
                {usageTimeseriesKeys.map((key, idx) => (
                  (() => {
                    const color = [
                      "#4f46e5",
                      "#06b6d4",
                      "#22c55e",
                      "#f59e0b",
                      "#ef4444",
                      "#a855f7",
                      "#14b8a6",
                      "#64748b",
                    ][idx % 8];
                    const name = key === "__other__" ? t("common.other", { defaultValue: "Overig" }) : key;
                    const isSelected = timeseriesSelectedKey === key;
                    const shouldFade = timeseriesSelectedKey && !isSelected;
                    return isSelected ? (
                      <Area
                        key={key}
                        type="monotone"
                        dataKey={key}
                        name={name}
                        stroke={color}
                        strokeWidth={3}
                        fill={color}
                        fillOpacity={0.12}
                        dot={false}
                        className="cursor-pointer"
                        onClick={() => toggleTimeseriesKey(key)}
                        animationDuration={350}
                        animationEasing="ease-out"
                        opacity={1}
                      />
                    ) : (
                      <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        name={name}
                        stroke={color}
                        strokeWidth={2}
                        dot={false}
                        className="cursor-pointer"
                        onClick={() => toggleTimeseriesKey(key)}
                        animationDuration={350}
                        animationEasing="ease-out"
                        opacity={shouldFade ? 0.35 : 1}
                      />
                    );
                  })()
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div ref={userEventsSectionRef} className="lumen-plain-card rounded-2xl p-6">
          <h3 className="text-sm font-semibold text-indigo-700 dark:text-indigo-200">
            {t("reports.analytics.user_events", { defaultValue: "User events" })}
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t("reports.analytics.user_events_hint", {
              defaultValue: "Bekijk per user welke pages/paths gebruikt worden (met filters en paging).",
            })}
          </p>

          <div className="mt-4 grid gap-3 md:grid-cols-6">
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {t("common.user", { defaultValue: "User" })}
              </label>
              <select
                value={userId}
                onChange={(e) => {
                  setUserId(e.target.value);
                  setTopUsersDrawerOpen(false);
                  setTopUsersDrawerUserId(null);
                  setUserPagesReportPlacement(null);
                  setUserDailyReportPlacement(null);
                }}
                disabled={usersLoading || users.length === 0}
                className="mt-1 w-full rounded-xl border border-indigo-100/70 bg-white/80 px-3 py-2 text-sm text-gray-900 shadow-sm disabled:opacity-60 dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-gray-100"
              >
                <option value="">
                  {usersLoading
                    ? t("common.loading", { defaultValue: "Bezig..." })
                    : users.length === 0
                      ? t("reports.analytics.users_empty", { defaultValue: "Geen users (of geen permissie)" })
                      : t("reports.analytics.select_user", { defaultValue: "Kies een user…" })}
                </option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.label}
                  </option>
                ))}
              </select>
              {usersError && (
                <p className="mt-2 text-xs text-amber-600 dark:text-amber-300">{usersError}</p>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {t("common.from", { defaultValue: "Van" })}
              </label>
              <input
                type="datetime-local"
                value={userEventsFromLocal}
                onChange={(e) => setUserEventsFromLocal(e.target.value)}
                className="mt-1 w-full rounded-xl border border-indigo-100/70 bg-white/80 px-3 py-2 text-sm text-gray-900 shadow-sm dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-gray-100"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {t("common.to", { defaultValue: "Tot" })}
              </label>
              <input
                type="datetime-local"
                value={userEventsToLocal}
                onChange={(e) => setUserEventsToLocal(e.target.value)}
                className="mt-1 w-full rounded-xl border border-indigo-100/70 bg-white/80 px-3 py-2 text-sm text-gray-900 shadow-sm dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-gray-100"
              />
            </div>
            <div className="md:col-span-1">
              <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {t("common.size", { defaultValue: "Size" })}
              </label>
              <input
                type="number"
                min={10}
                max={500}
                value={userEventsSize}
                onChange={(e) => setUserEventsSize(Number(e.target.value || 50))}
                className="mt-1 w-full rounded-xl border border-indigo-100/70 bg-white/80 px-3 py-2 text-sm text-gray-900 shadow-sm dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-gray-100"
              />
            </div>
            <div className="md:col-span-5 flex flex-wrap items-end justify-end gap-2">
              <button
                type="button"
                onClick={() => loadUserEvents({ pageIndex: 0 })}
                disabled={userEventsLoading || !userId.trim()}
                className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {userEventsLoading
                  ? t("common.loading", { defaultValue: "Bezig..." })
                  : t("common.search", { defaultValue: "Ophalen" })}
              </button>
              <button
                type="button"
                onClick={async () => {
                  setTopUsersDrawerOpen(false);
                  setTopUsersDrawerUserId(null);
                  setUserPagesReportPlacement("userEvents");
                  await loadUserPagesReport();
                }}
                disabled={userPagesLoading || !userId.trim()}
                className="rounded-xl border border-indigo-100/70 bg-white/80 px-4 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-indigo-200"
              >
                {userPagesLoading
                  ? t("common.loading", { defaultValue: "Bezig..." })
                  : t("reports.analytics.user_pages_report", { defaultValue: "Rapport: top pagina’s" })}
              </button>
              <button
                type="button"
                onClick={async () => {
                  setTopUsersDrawerOpen(false);
                  setTopUsersDrawerUserId(null);
                  setUserDailyReportPlacement("userEvents");
                  await loadUserDailyReport();
                }}
                disabled={userDailyLoading || !userId.trim()}
                className="rounded-xl border border-indigo-100/70 bg-white/80 px-4 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-indigo-200"
              >
                {userDailyLoading
                  ? t("common.loading", { defaultValue: "Bezig..." })
                  : t("reports.analytics.user_daily_report", { defaultValue: "Rapport: activiteit per dag" })}
              </button>
            </div>
          </div>

          {userEventsError && (
            <div className="mt-4 rounded-2xl border border-red-200/80 bg-red-50/90 px-4 py-3 text-sm text-red-800 shadow-sm dark:border-red-500/40 dark:bg-red-900/40 dark:text-red-100">
              {userEventsError}
            </div>
          )}

          {userPagesReportPlacement === "userEvents" && userId.trim() && userPagesCounts.length > 0 && (
            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              <div className="rounded-2xl border border-indigo-100/60 bg-white/70 p-4 shadow-sm dark:border-indigo-500/30 dark:bg-gray-900/40">
                <h4 className="text-sm font-semibold text-indigo-700 dark:text-indigo-200">
                  {t("reports.analytics.user_pages_title", { defaultValue: "Top pagina’s (user)" })}
                </h4>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t("reports.analytics.user_pages_hint", {
                    defaultValue: "Verdeling op basis van X-Lumen-Page (sampling tot 2000 events).",
                  })}
                </p>
                {userPagesError && (
                  <div className="mt-3 rounded-2xl border border-red-200/80 bg-red-50/90 px-4 py-3 text-sm text-red-800 shadow-sm dark:border-red-500/40 dark:bg-red-900/40 dark:text-red-100">
                    {userPagesError}
                  </div>
                )}
                <div className="relative mt-4 h-[260px] overflow-visible">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Tooltip allowEscapeViewBox={{ x: false, y: false }} wrapperStyle={{ zIndex: 50 }} />
                      <Legend />
                      <Pie
                        data={userPagesPieData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={55}
                        outerRadius={90}
                        paddingAngle={2}
                        activeIndex={userPagesActiveIndex ?? undefined}
                        onMouseEnter={(_, idx) => setUserPagesActiveIndex(idx)}
                        onMouseLeave={() => setUserPagesActiveIndex(null)}
                        activeShape={(props: any) => {
                          const {
                            cx,
                            cy,
                            innerRadius,
                            outerRadius,
                            startAngle,
                            endAngle,
                            fill,
                          } = props;
                          return (
                            <Sector
                              cx={cx}
                              cy={cy}
                              innerRadius={innerRadius}
                              outerRadius={outerRadius + 10}
                              startAngle={startAngle}
                              endAngle={endAngle}
                              fill={fill}
                            />
                          );
                        }}
                      >
                        {userPagesPieData.map((_, idx) => (
                          <Cell
                            key={idx}
                            fill={[
                              "#4f46e5",
                              "#06b6d4",
                              "#22c55e",
                              "#f59e0b",
                              "#ef4444",
                              "#a855f7",
                              "#14b8a6",
                              "#94a3b8",
                              "#64748b",
                            ][idx % 9]}
                          />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="rounded-2xl border border-indigo-100/60 bg-white/70 p-4 shadow-sm dark:border-indigo-500/30 dark:bg-gray-900/40">
                <h4 className="text-sm font-semibold text-indigo-700 dark:text-indigo-200">
                  {t("reports.analytics.user_pages_list_title", { defaultValue: "Top lijst" })}
                </h4>
                <ol className="mt-3 space-y-2 text-sm">
                  {userPagesCounts.slice(0, 12).map((it, idx) => (
                    <li key={`${it.label}-${idx}`} className="flex items-start justify-between gap-3">
                      <span className="min-w-0 break-words text-gray-800 dark:text-gray-100">
                        {idx + 1}. {it.label}
                      </span>
                      <span className="shrink-0 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200">
                        {it.count}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}

          {userDailyReportPlacement === "userEvents" && userId.trim() && (
            <div className="mt-6 rounded-2xl border border-indigo-100/60 bg-white/70 p-4 shadow-sm dark:border-indigo-500/30 dark:bg-gray-900/40">
              <h4 className="text-sm font-semibold text-indigo-700 dark:text-indigo-200">
                {t("reports.analytics.user_daily_title", { defaultValue: "Activiteit per dag (user)" })}
              </h4>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t("reports.analytics.user_daily_hint", {
                  defaultValue:
                    "Aantal geauthenticeerde API-requests per dag voor deze user (geen Keycloak login-events).",
                })}
              </p>
              {userDailyError && (
                <div className="mt-3 rounded-2xl border border-red-200/80 bg-red-50/90 px-4 py-3 text-sm text-red-800 shadow-sm dark:border-red-500/40 dark:bg-red-900/40 dark:text-red-100">
                  {userDailyError}
                </div>
              )}
              {userDailyLoading ? (
                <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                  {t("common.loading", { defaultValue: "Bezig..." })}
                </div>
              ) : userDailyCounts.length === 0 ? (
                <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">—</div>
              ) : (
                <>
                  <div className="mt-3 flex flex-wrap gap-4 text-xs text-gray-600 dark:text-gray-300">
                    <div>
                      {t("reports.analytics.user_daily_days", { defaultValue: "Actieve dagen" })}:{" "}
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        {typeof userDailySummary?.days === "number" ? userDailySummary.days : userDailyCounts.length}
                      </span>
                    </div>
                    <div>
                      {t("reports.analytics.user_daily_events", { defaultValue: "API-requests" })}:{" "}
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        {typeof userDailySummary?.total === "number"
                          ? userDailySummary.total
                          : userDailyCounts.reduce((s, it) => s + (it.count || 0), 0)}
                      </span>
                    </div>
                    {(userDailySummary?.firstDay || userDailySummary?.firstDate) && (
                      <div>
                        {t("reports.analytics.user_daily_first", { defaultValue: "Eerste dag" })}:{" "}
                        <span className="font-semibold text-gray-900 dark:text-gray-100">
                          {String(userDailySummary.firstDay || userDailySummary.firstDate)}
                        </span>
                      </div>
                    )}
                    {(userDailySummary?.lastDay || userDailySummary?.lastDate) && (
                      <div>
                        {t("reports.analytics.user_daily_last", { defaultValue: "Laatste dag" })}:{" "}
                        <span className="font-semibold text-gray-900 dark:text-gray-100">
                          {String(userDailySummary.lastDay || userDailySummary.lastDate)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 h-[220px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={userDailyCounts} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.18} />
                        <XAxis dataKey="day" tick={{ fontSize: 12 }} tickFormatter={(v) => formatShortNl(v)} />
                        <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                        <Tooltip
                          labelFormatter={(label: any) => formatShortNl(String(label))}
                          formatter={(value: any) => value}
                        />
                        <Bar dataKey="count" fill="#4f46e5" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="mt-4">
            <button
              type="button"
              onClick={() => setUserEventsExpanded((v) => !v)}
              className="inline-flex items-center gap-2 rounded-xl border border-indigo-100/70 bg-white/80 px-3 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-white dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-indigo-200"
              aria-expanded={userEventsExpanded}
            >
              {userEventsExpanded
                ? t("common.hide", { defaultValue: "Verbergen" })
                : t("common.show", { defaultValue: "Tonen" })}{" "}
              {t("reports.analytics.visited_links", { defaultValue: "Bezochte links" })}
              {userEventsTotal != null ? ` • ${userEventsTotal}` : userEvents.length ? ` • ${userEvents.length}` : ""}
            </button>
          </div>

          {userEventsExpanded && (
            <>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr className="bg-indigo-600/90 text-xs font-semibold uppercase tracking-wide text-white">
                      <th className="rounded-l-xl py-2 pl-4 pr-4">{t("common.time", { defaultValue: "Tijd" })}</th>
                      <th className="py-2 pr-4">Method</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4">Path</th>
                      <th className="py-2 pr-4">Page</th>
                      <th className="py-2 pr-4">Feature</th>
                      <th className="rounded-r-xl py-2 pr-4">{t("common.duration", { defaultValue: "Duur" })}</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-800 dark:text-gray-100">
                    {userEvents.length === 0 ? (
                      <tr>
                        <td className="py-3 text-gray-500 dark:text-gray-400" colSpan={7}>
                          —
                        </td>
                      </tr>
                    ) : (
                      userEvents.map((ev, idx) => {
                        const at =
                          (ev as any)?.at ||
                          (ev as any)?.createdAt ||
                          (ev as any)?.timestamp ||
                          (ev as any)?.time ||
                          (ev as any)?.occurredAt ||
                          (ev as any)?.eventAt ||
                          (ev as any)?.created_at ||
                          (ev as any)?.occurred_at ||
                          (ev as any)?.event_at ||
                          "";
                        const dur = ev?.durationMs ?? ev?.duration;
                        return (
                          <tr
                            key={`${at}-${idx}`}
                            className="border-t border-indigo-50/60 dark:border-indigo-500/20"
                          >
                            <td className="py-2 pl-4 pr-4 whitespace-nowrap">{formatDateTimeNl(at)}</td>
                            <td className="py-2 pr-4 whitespace-nowrap">{ev?.method || "—"}</td>
                            <td className="py-2 pr-4 whitespace-nowrap">
                              {typeof ev?.status === "number" ? String(ev.status) : "—"}
                            </td>
                            <td className="py-2 pr-4 break-words">{ev?.path || "—"}</td>
                            <td className="py-2 pr-4 break-words">{ev?.page || "—"}</td>
                            <td className="py-2 pr-4 break-words">{ev?.feature || "—"}</td>
                            <td className="py-2 pr-4 whitespace-nowrap">
                              {typeof dur === "number" && Number.isFinite(dur) ? `${dur}ms` : "—"}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600 dark:text-gray-300">
                <div>
                  {t("common.page", { defaultValue: "Pagina" })} {userEventsPageIndex + 1}
                  {userEventsTotal != null ? ` • total: ${userEventsTotal}` : ""}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => loadUserEvents({ pageIndex: Math.max(0, userEventsPageIndex - 1) })}
                    disabled={userEventsLoading || userEventsPageIndex <= 0}
                    className="rounded-xl border border-indigo-100/70 bg-white/80 px-3 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-indigo-200"
                  >
                    {t("common.prev", { defaultValue: "Vorige" })}
                  </button>
                  <button
                    type="button"
                    onClick={() => loadUserEvents({ pageIndex: userEventsPageIndex + 1 })}
                    disabled={userEventsLoading || userEvents.length < userEventsSize}
                    className="rounded-xl border border-indigo-100/70 bg-white/80 px-3 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-indigo-200"
                  >
                    {t("common.next", { defaultValue: "Volgende" })}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </PageTemplate>
  );
}
